import React, { createContext, useContext, useState, useRef } from 'react';
import { notifyError } from '@nutastic/nutastic-react-app';

const PLAYER_MODES = {
  PLAYLIST: 'PLAYLIST',
  RADIO: 'RADIO'
};

/**
 * @typedef NuPlayerTrack
 * @property {string} title - name of track
 * @property {string} src - file reference for track
 * @property {string?} author - artist / creator name
 * @property {string?} parentTitle - album / playlist name
 * @property {string?} img - cover image
 * @property {string?} thumbnail - small cover image
 */

/**
 * @typedef NuPlayerContextValues
 * @property {function({ tracksToLoad: [], trackIndex: number })} insertDiscAndPressPlay - receives new track list and begins playing the music
 * @property {NuPlayerTrack[]} tracks - list of tracks in the deck
 * @property {function([])} setTracks - set the list of tracks in the deck
 * @property {NuPlayerTrack} currentTrack - active track in the deck
 * @property {function(NuPlayerTrack)} setCurrentTrack - change the current track
 * @property {number} timeProgress - progress timer
 * @property {function(number)} setTimeProgress - change progress timer
 * @property {number} duration - current track total time
 * @property {function(number)} setDuration - change duration time
 * @property {function(number)} setTrackIndex - change track
 * @property {any} audioRef - HTML audio element reference
 * @property {any} progressBarRef - HTML reference to progress bar
 * @property {boolean} isPlaying - true if track is playing
 * @property {function(boolean)} setIsPlaying - change player state
 * @property {function} onLoadBroadcast - function to call when loading playing a broadcast again (if needed)
 * @property {function(string)} setPlayerMode - change mode of player
 * @property {string} playerMode - current player mode
 * @property {function(string)} setPlayerMode - change mode of player
 * @property {string} playerMode - current player mode
 * @property {function(object)} setPlayerConfigData - change player configuration data
 * @property {object} playerConfigData - current player configuration data
 * @property {{PLAYLIST: string, RADIO: string}} PLAYER_MODES - player mode constants
 * @property {function} stopTheMusic - stops the music player and closes it
 */

/**
 *
 * @type NuPlayerContextValues
 */
const defaultValue = {};
const NuPlayerContext = createContext(defaultValue);

const NuPlayerProvider = ({ children, initialTracks, onLoadBroadcast }) => {
  const [playerConfigData, setPlayerConfigData] = useState({});
  const [playerMode, setPlayerMode] = useState(PLAYER_MODES.PLAYLIST);
  const [tracks, setTracks] = useState(initialTracks || null);
  const [trackIndex, setTrackIndex] = useState(0);
  const [currentTrack, setCurrentTrack] = useState((tracks && tracks.length > 0) ? tracks[0] : null);
  const [timeProgress, setTimeProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  const insertDiscAndPressPlay = ({ tracksToLoad, trackIndex, isRadioMode }) => {
    if (isRadioMode) {
      setPlayerMode(PLAYER_MODES.RADIO);
    } else {
      setPlayerMode(PLAYER_MODES.PLAYLIST);
    }
    const trackIndexToPlay = Array.isArray(tracksToLoad) && tracksToLoad.length > 0 && trackIndex && trackIndex <= tracksToLoad.length ? trackIndex : 0;
    setIsPlaying(false);
    setTrackIndex(trackIndexToPlay);
    setTracks([...tracksToLoad]);
    setCurrentTrack((tracksToLoad && tracksToLoad.length > 0) ? tracksToLoad[trackIndexToPlay] : null);
    setTimeout(() => {
      setIsPlaying(true);
    }, 500);
  };

  const onLoadBroadcastClicked = async (configData) => {
    const onComplete = ({ tracksToLoad, targetTrackIndex, trackLandingTime }) => {
      insertDiscAndPressPlay({ tracksToLoad, trackIndex: targetTrackIndex, isRadioMode: true });
      if(audioRef.current) {
        setTimeout(() => audioRef.current.currentTime = trackLandingTime, 500);
      }
      configData && setPlayerConfigData(configData);
    }
    const onFail = message => {
      notifyError('loadBroadcastError', 'Uh Oh!', message);
    }
    await onLoadBroadcast(configData||playerConfigData, onComplete, onFail);
  };

  const onSetIsPlaying = v => {
    if(v && !tracks) {
      alert('There are no tracks to play bro...try again!');
    } else {
      setIsPlaying(v);
    }
  };

  const onSetTrackIndex = v => {
    if(v >= 0 && v < tracks.length) {
      setTrackIndex(v);
      setCurrentTrack(tracks[v]);
    } else {
      console.warn('Invalid track index');
    }
  };

  const stopTheMusic = () => {
    setIsPlaying(false);
    setTracks(null);
    setTrackIndex(0);
    setCurrentTrack(null);
    setTimeProgress(0);
    setDuration(0);
  };

  const contextValue = {
    tracks,
    setTracks,
    currentTrack,
    setCurrentTrack,
    audioRef,
    progressBarRef,
    timeProgress,
    setTimeProgress,
    duration,
    setDuration,
    setTrackIndex: onSetTrackIndex,
    trackIndex,
    isPlaying,
    setIsPlaying: onSetIsPlaying,
    insertDiscAndPressPlay,
    onLoadBroadcast: onLoadBroadcastClicked,
    playerConfigData,
    setPlayerConfigData,
    setPlayerMode,
    playerMode,
    PLAYER_MODES: PLAYER_MODES,
    stopTheMusic
  };

  return (
    <NuPlayerContext.Provider value={contextValue}>
      {children}
    </NuPlayerContext.Provider>
  );
};

/**
 *
 * @returns {NuPlayerContextValues}
 */
const useNuPlayerContext = () => {
  const context = useContext(NuPlayerContext);
  if (context === undefined) {
    throw new Error(
      'useNuPlayerContext must be used within an NuPlayerProvider'
    );
  }
  return context;
};

export {
  NuPlayerProvider,
  useNuPlayerContext
};
